import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";
import { FadeUp } from "../Reveal";
import { experience } from "../../data/content";

const NODE_GAP = 96; // px between nodes
const DOT_HALF = 6;  // half of w-3 h-3 (12px) circle  -  used for centering + mask extension

/**
 * Unified timeline: ONE asset where the track and circles share the same colored
 * gradient that "fills up" as the user scrolls.
 *
 * ── ROOT CAUSES OF MISSING CIRCLES ──
 *
 * BUG 1  -  Duplicate React keys:
 *   key={item.period} gave key="2025" to two list items (ATL and First Global Bank).
 *   React reconciles them as the same element  -  one <li> never mounts its ref,
 *   leaving itemRefs.current with a sparse hole at that index.
 *   FIX: key={`exp-${i}`}  -  unique per index, safe since items never reorder.
 *
 * BUG 2  -  Sparse array.map() producing short dotTops:
 *   measure() called itemRefs.current.map(...). Array.prototype.map SKIPS sparse
 *   holes, so the resulting array was shorter than experience.length. Circles at
 *   the out-of-bounds indices got top:"undefinedpx" and disappeared.
 *   FIX: map over experience (stable length) rather than itemRefs.current.
 *
 * BUG 3  -  Safari offsetParent breakage:
 *   Framer-motion sets CSS transform on its wrapper <div>. Safari treats
 *   transformed elements as offsetParent candidates (Chrome does not). This made
 *   li.offsetTop return 0 for every item in Safari, stacking all circles at the top.
 *   FIX: walk the offsetParent chain manually up to ref.current, accumulating
 *   offsetTop at each step. This is transform-independent and correct in both engines.
 */
export const Experience = () => {
  const ref      = useRef(null);   // scroll-tracking target + measurement root
  const itemRefs = useRef([]);     // one ref per <li>

  // Initial guess keeps SSR/first-paint reasonable; replaced after mount.
  const [dotTops, setDotTops] = useState(
    experience.map((_, i) => i * NODE_GAP)
  );

  // ── Scroll-driven fill ──────────────────────────────────────────────────────
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 90%", "end 15%"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["-10%", "110%"]);
  const dotsMask   = useMotionTemplate`linear-gradient(to bottom, #000 ${lineHeight}, rgba(0,0,0,0.18) ${lineHeight})`;

  // ── Walk offsetParent chain from el up to container ─────────────────────────
  // This is transform-independent: Safari treats CSS-transformed elements as
  // offsetParent candidates, Chrome does not. Walking the chain accumulates
  // offsetTop at every step and terminates at container regardless of engine.
  const getTopRelativeTo = useCallback((el, container) => {
    let top = 0;
    let node = el;
    while (node && node !== container) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    return top;
  }, []);

  // ── Measurement ─────────────────────────────────────────────────────────────
  // Always maps over experience (fixed length) so dotTops never becomes shorter
  // than experience.length. Falls back to the initial estimate if a ref is missing.
  const measure = useCallback(() => {
    if (!ref.current) return;
    // Wait until every ref has been populated (all <li>s have mounted)
    const allReady = experience.every((_, i) => itemRefs.current[i] != null);
    if (!allReady) return;

    setDotTops(
      experience.map((_, i) => {
        const el = itemRefs.current[i];
        return el ? getTopRelativeTo(el, ref.current) : i * NODE_GAP;
      })
    );
  }, [getTopRelativeTo]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (ref.current) ro.observe(ref.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <section id="experience" className="relative py-20 sm:py-28 border-t border-edge-650">
      <div className="container-page">
        <FadeUp>
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-magenta-300">Experience</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="mt-4 font-display tracking-tight text-3xl sm:text-5xl text-white max-w-3xl leading-[1.05]">
            A few years of turning ideas into reality.
          </h2>
        </FadeUp>

        {/* ref = scroll target + offsetTop measurement root */}
        <div ref={ref} className="relative mt-12 max-w-3xl">

          {/* ── Timeline rail ─────────────────────────────────────────────── */}
          <div className="absolute left-0 top-0 bottom-0 w-3 pointer-events-none" aria-hidden>

            {/* Dim base track */}
            <span className="absolute left-[5px] top-0 bottom-0 w-px bg-edge-700" />

            {/* Dim outline rings  -  pixel-exact positions from measurement */}
            {experience.map((_, i) => (
              <span
                key={`ring-dim-${i}`}
                className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border border-edge-700 bg-ink-950"
                style={{ top: `${dotTops[i] - DOT_HALF}px` }}
              />
            ))}

            {/*
              ACTIVE LAYER
              ─────────────────────────────────────────────────────────────────
              Extended DOT_HALF px above and below the parent so the first and
              last circles are FULLY inside the masked area (no edge clipping).

              Coordinate maths for active ring `top`:
                • Extended container top  = parent top − DOT_HALF
                • To centre a circle on dotTops[i] (parent coords):
                    extended_y_centre = dotTops[i] + DOT_HALF   (shift for extension)
                    ring top          = extended_y_centre − DOT_HALF  (centre → top)
                                      = dotTops[i]               ← simplifies perfectly
              So active ring top = dotTops[i] px in the extended container.  ✓
            */}
            <motion.div
              className="absolute left-0 right-0"
              style={{
                top:    -DOT_HALF,
                bottom: -DOT_HALF,
                WebkitMaskImage: dotsMask,
                maskImage:       dotsMask,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat:       "no-repeat",
              }}
            >
              {/* Active filled line  -  spans the full extended area; mask controls visibility */}
              <span className="absolute left-[5px] top-0 bottom-0 w-px bg-magenta-500" />

              {/* Active filled rings */}
              {experience.map((_, i) => (
                <span
                  key={`ring-active-${i}`}
                  className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-magenta-500 shadow-[0_0_0_4px_rgba(220,38,38,0.20)]"
                  style={{ top: `${dotTops[i]}px` }}
                />
              ))}
            </motion.div>
          </div>

          {/* ── Experience nodes ───────────────────────────────────────────── */}
          {/*
            FIX: key={`exp-${i}`} instead of key={item.period}.
            Two items share period "2025"  -  duplicate keys caused React to
            reconcile them as one element, leaving one <li> ref unset (sparse hole)
            and that circle invisible. Index-based keys are safe here since the
            experience array never reorders at runtime.
          */}
          <ol className="relative pl-10 sm:pl-14">
            {experience.map((item, i) => (
              <FadeUp key={`exp-${i}`} delay={i * 0.05}>
                <li
                  ref={(el) => { itemRefs.current[i] = el; }}
                  className="relative"
                  style={{ paddingBottom: i === experience.length - 1 ? 0 : NODE_GAP }}
                >
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">{item.period}</div>
                  <div className="mt-1 font-display text-xl sm:text-2xl text-white tracking-tight">
                    {item.role} <span className="text-zinc-400">· {item.org}</span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {item.points.map((p) => (
                      <li key={p} className="flex gap-3 text-sm text-zinc-300">
                        <span className="mt-2 inline-block w-1 h-1 rounded-full bg-zinc-500 shrink-0" />{p}
                      </li>
                    ))}
                  </ul>
                </li>
              </FadeUp>
            ))}
          </ol>

        </div>
      </div>
    </section>
  );
};
